import React, { useState, useEffect, useMemo, useRef } from 'react';
import { GraduationCap, Briefcase, Zap, FileUp, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './ProfileModal.css';

const PROFICIENCY = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
const DEGREES = ["High School Diploma","Associate Degree","Bachelor's Degree","Master's Degree","Doctorate (PhD)","Professional Degree (JD/MD)","Certificate","Diploma","Other"];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const YEARS = Array.from({ length: 40 }, (_, i) => new Date().getFullYear() - i);

const emptyEdu   = () => ({ school: '', degree: '', field: '', startYear: '', endYear: '', current: false, honors: '' });
const emptyExp   = () => ({ company: '', role: '', startYear: '', startMonth: '', endYear: '', endMonth: '', current: false });
const emptySkill = () => ({ name: '', yearsOfExperience: 1, proficiency: 'Intermediate' });

const EMPTY_PROFILE = { education: [], experience: [], skills: [], summary: '' };

// Deep equality check for dirty detection
const deepEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// ── View components (read-only) ───────────────────────────────────────────────
const EduView = ({ items }) => {
  if (!items.length) return <div className="pmodal-empty-inline">No education added.</div>;
  return items.map((e, i) => (
    <div key={i} className="pview-card">
      <div className="pview-main">
        <div className="pview-title">{e.degree || 'Degree'}{e.field ? ` in ${e.field}` : ''}</div>
        <div className="pview-sub">{e.school}</div>
      </div>
      <div className="pview-meta">
        {(e.startYear || e.endYear) && (
          <span>{e.startYear || '?'} – {e.current ? 'Present' : (e.endYear || '?')}</span>
        )}
        {e.honors && <span className="pview-badge">{e.honors}</span>}
      </div>
    </div>
  ));
};

const ExpView = ({ items }) => {
  if (!items.length) return <div className="pmodal-empty-inline">No work experience added.</div>;
  return items.map((e, i) => (
    <div key={i} className="pview-card">
      <div className="pview-main">
        <div className="pview-title">{e.role}</div>
        <div className="pview-sub">{e.company}</div>
      </div>
      <div className="pview-meta">
        {(e.startYear || e.endYear) && (
          <span>
            {e.startMonth ? `${MONTHS[e.startMonth-1]} ` : ''}{e.startYear || '?'} –{' '}
            {e.current ? 'Present' : `${e.endMonth ? MONTHS[e.endMonth-1] + ' ' : ''}${e.endYear || '?'}`}
          </span>
        )}
      </div>
    </div>
  ));
};

const SkillsView = ({ items }) => {
  if (!items.length) return <div className="pmodal-empty-inline">No skills added.</div>;
  const PROF_COLOR = { Beginner: '#6B7280', Intermediate: '#2563EB', Advanced: '#7C3AED', Expert: '#10B981' };
  return (
    <div className="pview-skills-grid">
      {items.map((s, i) => (
        <div key={i} className="pview-skill-chip">
          <span className="pview-skill-name">{s.name}</span>
          <span className="pview-skill-prof" style={{ color: PROF_COLOR[s.proficiency] }}>{s.proficiency}</span>
          <span className="pview-skill-yrs">{s.yearsOfExperience} yr{s.yearsOfExperience !== 1 ? 's' : ''}</span>
        </div>
      ))}
    </div>
  );
};

// ── Main ProfileModal ─────────────────────────────────────────────────────────
const ProfileModal = ({ onClose, onUpgrade }) => {
  const [tab, setTab] = useState('education');
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [savedProfile, setSavedProfile] = useState(EMPTY_PROFILE); // snapshot of last saved state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [parsingResume, setParsingResume] = useState(false);
  const [pendingResume, setPendingResume] = useState(null);
  const avatarInputRef = useRef(null);
  const resumeInputRef = useRef(null);
  const { user, login } = useAuth();
  const isPro = user?.tier === 'pro';

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    if (user) setNameValue(user.name || '');
  }, [user]);

  useEffect(() => {
    api.get('/api/profile').then(r => {
      const p = r.data.profile;
      setProfile(p);
      setSavedProfile(JSON.parse(JSON.stringify(p)));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const isDirty = useMemo(() => !deepEqual(profile, savedProfile), [profile, savedProfile]);

  const save = async () => {
    if (!isDirty) return;
    setSaving(true);
    try {
      const r = await api.put('/api/profile', profile);
      const saved = r.data.profile;
      setProfile(saved);
      setSavedProfile(JSON.parse(JSON.stringify(saved)));
      setEditMode(false);
      toast.success('Profile saved!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setProfile(JSON.parse(JSON.stringify(savedProfile)));
    setEditMode(false);
  };

  // ── Edu helpers ──
  const addEdu    = () => setProfile(p => ({ ...p, education: [...p.education, emptyEdu()] }));
  const setEdu    = (i, k, v) => setProfile(p => ({ ...p, education: p.education.map((e, idx) => idx === i ? { ...e, [k]: v } : e) }));
  const removeEdu = (i) => setProfile(p => ({ ...p, education: p.education.filter((_, idx) => idx !== i) }));

  // ── Exp helpers ──
  const addExp    = () => setProfile(p => ({ ...p, experience: [...p.experience, emptyExp()] }));
  const setExp    = (i, k, v) => setProfile(p => ({ ...p, experience: p.experience.map((e, idx) => idx === i ? { ...e, [k]: v } : e) }));
  const removeExp = (i) => setProfile(p => ({ ...p, experience: p.experience.filter((_, idx) => idx !== i) }));

  // ── Skill helpers ──
  const addSkill    = () => setProfile(p => ({ ...p, skills: [...p.skills, emptySkill()] }));
  const setSkill    = (i, k, v) => setProfile(p => ({ ...p, skills: p.skills.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const removeSkill = (i) => setProfile(p => ({ ...p, skills: p.skills.filter((_, idx) => idx !== i) }));

  const saveName = async () => {
    if (!nameValue.trim() || nameValue.trim() === user?.name) { setEditingName(false); return; }
    setSavingName(true);
    try {
      const r = await api.put('/api/auth/me', { name: nameValue.trim() });
      // Update auth context token so user.name updates globally
      const token = localStorage.getItem('token');
      login(token, r.data.user);
      toast.success('Name updated!');
      setEditingName(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update name');
    } finally { setSavingName(false); }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Resize to max 256×256 JPEG and encode as base64
    const img = new Image();
    img.onload = async () => {
      const SIZE = 256;
      const scale = Math.min(SIZE / img.width, SIZE / img.height, 1);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      const base64 = canvas.toDataURL('image/jpeg', 0.82);
      setUploadingAvatar(true);
      try {
        const r = await api.put('/api/auth/me', { avatar: base64 });
        const token = localStorage.getItem('token');
        login(token, r.data.user);
        toast.success('Profile photo updated!');
      } catch {
        toast.error('Failed to update photo');
      } finally {
        setUploadingAvatar(false);
        e.target.value = '';
      }
    };
    img.src = URL.createObjectURL(file);
  };

  const applyResumeData = (data, mode) => {
    if (mode === 'replace') {
      setProfile(p => ({
        ...p,
        education: data.education || [],
        experience: data.experience || [],
        skills: data.skills || [],
      }));
    } else {
      setProfile(p => ({
        ...p,
        education: [...p.education, ...(data.education || [])],
        experience: [...p.experience, ...(data.experience || [])],
        skills: [...p.skills, ...(data.skills || [])],
      }));
    }
    setPendingResume(null);
    setEditMode(true);
    toast.success('Resume data applied — review and save when ready.');
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setParsingResume(true);
    try {
      const form = new FormData();
      form.append('resume', file);
      const r = await api.post('/api/profile/parse-resume', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const parsed = r.data;
      const hasExisting = profile.education.length > 0 || profile.experience.length > 0 || profile.skills.length > 0;
      if (hasExisting) {
        setPendingResume(parsed);
      } else {
        applyResumeData(parsed, 'replace');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to parse resume');
    } finally {
      setParsingResume(false);
    }
  };

  const TABS = [
    { id: 'education', label: 'Education',  icon: <GraduationCap size={14} />, count: profile.education.length },
    { id: 'experience', label: 'Experience', icon: <Briefcase size={14} />, count: profile.experience.length },
    { id: 'skills',    label: 'Skills',      icon: <Zap size={14} />, count: profile.skills.length },
  ];

  return (
    <div className="pmodal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pmodal-panel">

        {/* Header */}
        <div className="pmodal-header">
          <div
            className="pmodal-header-avatar"
            onClick={() => avatarInputRef.current?.click()}
            title="Change profile photo"
          >
            {user?.avatar
              ? <img src={user.avatar} alt={user.name} className="pmodal-avatar-img" />
              : <span className="pmodal-avatar-initials">{user?.name?.[0]?.toUpperCase()}</span>
            }
            <div className="pmodal-avatar-overlay">
              {uploadingAvatar
                ? <span className="btn-spinner" style={{ borderColor: 'rgba(255,255,255,0.4)', borderTopColor: 'white' }} />
                : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
              }
            </div>
            <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
          </div>
          <div className="pmodal-header-text">
            {editingName ? (
              <div className="pmodal-name-edit-row">
                <input
                  className="pmodal-name-input"
                  value={nameValue}
                  onChange={e => setNameValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditingName(false); }}
                  autoFocus
                />
                <button className="pmodal-name-save" onClick={saveName} disabled={savingName}>
                  {savingName ? '...' : '✓'}
                </button>
                <button className="pmodal-name-cancel" onClick={() => { setEditingName(false); setNameValue(user?.name || ''); }}>✕</button>
              </div>
            ) : (
              <div className="pmodal-name-row">
                <h2 className="pmodal-title">{user?.name || 'My Profile'}</h2>
                <button className="pmodal-name-edit-btn" onClick={() => setEditingName(true)} title="Edit name">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
              </div>
            )}
            <p className="pmodal-sub">Used by AI to personalize analysis and comparisons</p>
          </div>
          <div className="pmodal-header-right">
            <button
              className={`pmodal-resume-upload-btn ${!isPro ? 'pmodal-resume-locked' : ''}`}
              onClick={() => isPro ? resumeInputRef.current?.click() : onUpgrade?.()}
              disabled={parsingResume}
              title={!isPro ? 'Upload Resume requires a Pro plan' : 'Upload resume PDF to auto-fill profile'}
            >
              {!isPro
                ? <Lock size={13} />
                : parsingResume
                  ? <span className="btn-spinner" style={{ borderColor: 'rgba(37,99,235,0.3)', borderTopColor: 'var(--primary)' }} />
                  : <FileUp size={13} />
              }
              {parsingResume ? 'Parsing...' : 'Upload Resume'}
              {!isPro && <span className="pmodal-resume-pro-badge">Pro</span>}
            </button>
            <input ref={resumeInputRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={handleResumeUpload} />
            {!editMode ? (
              <button className="pmodal-edit-toggle" onClick={() => setEditMode(true)}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
                Edit
              </button>
            ) : (
              <button className="pmodal-edit-toggle pmodal-edit-cancel" onClick={cancelEdit}>
                Cancel
              </button>
            )}
            <button className="pmodal-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Resume merge confirmation banner */}
        {pendingResume && (
          <div className="pmodal-resume-merge-banner">
            <div className="pmodal-resume-merge-msg">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
              </svg>
              Resume parsed. You have existing profile data — how should we apply it?
            </div>
            <div className="pmodal-resume-merge-actions">
              <button className="pmodal-merge-replace" onClick={() => applyResumeData(pendingResume, 'replace')}>Replace All</button>
              <button className="pmodal-merge-add" onClick={() => applyResumeData(pendingResume, 'merge')}>Add to Existing</button>
              <button className="pmodal-merge-cancel" onClick={() => setPendingResume(null)}>Cancel</button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="pmodal-tabs">
          {TABS.map(t => (
            <button key={t.id} className={`pmodal-tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
              <span>{t.icon}</span>
              {t.label}
              {t.count > 0 && <span className="pmodal-tab-count">{t.count}</span>}
              {/* Dirty indicator per section */}
              {isDirty && tab !== t.id && !deepEqual(profile[t.id === 'education' ? 'education' : t.id === 'experience' ? 'experience' : 'skills'], savedProfile[t.id === 'education' ? 'education' : t.id === 'experience' ? 'experience' : 'skills']) && (
                <span className="pmodal-dirty-dot" />
              )}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="pmodal-body">
          {loading ? (
            <div className="pmodal-loading"><div className="loading-spinner" /> Loading profile...</div>
          ) : (
            <>
              {/* ── VIEW MODE ── */}
              {!editMode && (
                <div className="pmodal-view-section">
                  {tab === 'education'  && <EduView   items={profile.education} />}
                  {tab === 'experience' && <ExpView   items={profile.experience} />}
                  {tab === 'skills'     && <SkillsView items={profile.skills} />}

                  {/* Empty state with prompt to edit */}
                  {((tab === 'education' && !profile.education.length) ||
                    (tab === 'experience' && !profile.experience.length) ||
                    (tab === 'skills' && !profile.skills.length)) && (
                    <div className="pview-empty-cta">
                      <button className="pview-edit-btn" onClick={() => setEditMode(true)}>
                        + Add {tab === 'education' ? 'Education' : tab === 'experience' ? 'Experience' : 'Skills'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* ── EDIT MODE ── */}
              {editMode && (
                <>
                  {/* EDUCATION */}
                  {tab === 'education' && (
                    <div className="pmodal-section">
                      <div className="pmodal-section-top">
                        <p className="pmodal-section-hint">Your educational background.</p>
                        <button className="pmodal-add-btn" onClick={addEdu}>+ Add Education</button>
                      </div>
                      {profile.education.length === 0 && <div className="pmodal-empty">No education yet. Click "+ Add Education".</div>}
                      {profile.education.map((edu, i) => (
                        <div key={i} className="pmodal-card">
                          <button className="pmodal-remove-btn" onClick={() => removeEdu(i)}>✕</button>
                          <div className="pmodal-grid-2">
                            <div className="pf">
                              <label>School / University</label>
                              <input className="pinput" value={edu.school} onChange={e => setEdu(i,'school',e.target.value)} placeholder="e.g. University of the Philippines" />
                            </div>
                            <div className="pf">
                              <label>Degree</label>
                              <select className="pselect" value={edu.degree} onChange={e => setEdu(i,'degree',e.target.value)}>
                                <option value="">Select degree...</option>
                                {DEGREES.map(d => <option key={d} value={d}>{d}</option>)}
                              </select>
                            </div>
                          </div>
                          <div className="pmodal-grid-2">
                            <div className="pf">
                              <label>Field of Study</label>
                              <input className="pinput" value={edu.field} onChange={e => setEdu(i,'field',e.target.value)} placeholder="e.g. Computer Science" />
                            </div>
                            <div className="pf">
                              <label>Honors / Awards</label>
                              <input className="pinput" value={edu.honors} onChange={e => setEdu(i,'honors',e.target.value)} placeholder="e.g. Cum Laude" />
                            </div>
                          </div>
                          <div className="pmodal-grid-3">
                            <div className="pf">
                              <label>Start Year</label>
                              <select className="pselect" value={edu.startYear} onChange={e => setEdu(i,'startYear',e.target.value)}>
                                <option value="">Year</option>
                                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                              </select>
                            </div>
                            <div className="pf">
                              <label>End Year</label>
                              <select className="pselect" value={edu.current ? '' : edu.endYear} onChange={e => setEdu(i,'endYear',e.target.value)} disabled={edu.current}>
                                <option value="">Year</option>
                                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                              </select>
                            </div>
                            <div className="pf pf-checkbox">
                              <label className="pcheckbox-label">
                                <input type="checkbox" checked={edu.current} onChange={e => setEdu(i,'current',e.target.checked)} />
                                Currently enrolled
                              </label>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* EXPERIENCE */}
                  {tab === 'experience' && (
                    <div className="pmodal-section">
                      <div className="pmodal-section-top">
                        <p className="pmodal-section-hint">Your work history.</p>
                        <button className="pmodal-add-btn" onClick={addExp}>+ Add Job</button>
                      </div>
                      {profile.experience.length === 0 && <div className="pmodal-empty">No experience yet. Click "+ Add Job".</div>}
                      {profile.experience.map((exp, i) => (
                        <div key={i} className="pmodal-card">
                          <button className="pmodal-remove-btn" onClick={() => removeExp(i)}>✕</button>
                          <div className="pmodal-grid-2">
                            <div className="pf">
                              <label>Company</label>
                              <input className="pinput" value={exp.company} onChange={e => setExp(i,'company',e.target.value)} placeholder="e.g. Company X" />
                            </div>
                            <div className="pf">
                              <label>Role / Title</label>
                              <input className="pinput" value={exp.role} onChange={e => setExp(i,'role',e.target.value)} placeholder="e.g. Software Engineer" />
                            </div>
                          </div>
                          <div className="pmodal-grid-4">
                            <div className="pf">
                              <label>Start Month</label>
                              <select className="pselect" value={exp.startMonth} onChange={e => setExp(i,'startMonth',e.target.value)}>
                                <option value="">Month</option>
                                {MONTHS.map((m,idx) => <option key={m} value={idx+1}>{m}</option>)}
                              </select>
                            </div>
                            <div className="pf">
                              <label>Start Year</label>
                              <select className="pselect" value={exp.startYear} onChange={e => setExp(i,'startYear',e.target.value)}>
                                <option value="">Year</option>
                                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                              </select>
                            </div>
                            <div className="pf">
                              <label>End Month</label>
                              <select className="pselect" value={exp.current ? '' : exp.endMonth} onChange={e => setExp(i,'endMonth',e.target.value)} disabled={exp.current}>
                                <option value="">Month</option>
                                {MONTHS.map((m,idx) => <option key={m} value={idx+1}>{m}</option>)}
                              </select>
                            </div>
                            <div className="pf">
                              <label>End Year</label>
                              <select className="pselect" value={exp.current ? '' : exp.endYear} onChange={e => setExp(i,'endYear',e.target.value)} disabled={exp.current}>
                                <option value="">Year</option>
                                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                              </select>
                            </div>
                          </div>
                          <div className="pf pf-checkbox">
                            <label className="pcheckbox-label">
                              <input type="checkbox" checked={exp.current} onChange={e => setExp(i,'current',e.target.checked)} />
                              Currently working here
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* SKILLS */}
                  {tab === 'skills' && (
                    <div className="pmodal-section">
                      <div className="pmodal-section-top">
                        <p className="pmodal-section-hint">Technical and professional skills.</p>
                        <button className="pmodal-add-btn" onClick={addSkill}>+ Add Skill</button>
                      </div>
                      {profile.skills.length === 0 && <div className="pmodal-empty">No skills yet. Click "+ Add Skill".</div>}
                      <div className="skills-grid">
                        {profile.skills.map((skill, i) => (
                          <div key={i} className="pmodal-skill-card">
                            <button className="pmodal-remove-btn" onClick={() => removeSkill(i)}>✕</button>
                            <div className="pf">
                              <label>Skill Name</label>
                              <input className="pinput" value={skill.name} onChange={e => setSkill(i,'name',e.target.value)} placeholder="e.g. SQL, React, Excel" />
                            </div>
                            <div className="pf">
                              <label>Proficiency</label>
                              <div className="proficiency-btns">
                                {PROFICIENCY.map(p => (
                                  <button key={p} className={`prof-btn ${skill.proficiency === p ? 'active' : ''}`} onClick={() => setSkill(i,'proficiency',p)}>{p}</button>
                                ))}
                              </div>
                            </div>
                            <div className="pf">
                              <label>Years of Experience: <strong>{skill.yearsOfExperience}</strong></label>
                              <input type="range" min="0" max="20" step="0.5" value={skill.yearsOfExperience}
                                onChange={e => setSkill(i,'yearsOfExperience',parseFloat(e.target.value))} className="years-slider" />
                              <div className="slider-labels"><span>0 yrs</span><span>10 yrs</span><span>20 yrs</span></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="pmodal-footer">
          <span className="pmodal-footer-hint">
            {isDirty ? '⚠️ You have unsaved changes.' : '💡 Used by AI to personalize analysis.'}
          </span>
          <div className="pmodal-footer-actions">
            <button className="pmodal-cancel-btn" onClick={onClose}>Close</button>
            <button
              className={`pmodal-save-btn ${isDirty ? 'pmodal-save-active' : 'pmodal-save-disabled'}`}
              onClick={save}
              disabled={!isDirty || saving}
            >
              {saving ? <><span className="btn-spinner" /> Saving...</> : '✓ Save Profile'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;
